/**
 * The bag (sections/cart-drawer.liquid).
 * <cart-drawer>: opens at once on cart:added (and from any [aria-controls="CartDrawer"] link), then
 * re-renders its body in place through the Section Rendering API, as after every change, and turns
 * the quantity links into optimistic steps: the new quantity shows at once, the request runs in order behind any
 * earlier one, and on error the old quantity comes back with the reason inline.
 * Each opening fires `cart:open` on document (analytics-commerce.js sends view_cart).
 * The drawer can be dragged off to the right; it follows the pointer 1:1 and closes when the
 * release, projected forward by its velocity, lands past 40% of its width.
 */
import { CART_ADDED, CART_UPDATED, changeCartLine, defineElement, project } from '@wispaire/global';

const Base = globalThis.HTMLElement ?? class {};

export class CartDrawer extends Base {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.status = this.querySelector('[data-cart-status]');
    this.queue = Promise.resolve();

    this.onAdded = () => {
      this.open();
      this.refresh();
    };
    this.onUpdated = (event) => {
      if (event.detail?.source !== this) this.refresh();
    };
    this.onTrigger = (event) => {
      const trigger = event.target.closest?.('[aria-controls="CartDrawer"]');
      if (!trigger || !this.dialog) return;
      event.preventDefault();
      this.open(trigger);
    };
    document.addEventListener(CART_ADDED, this.onAdded);
    document.addEventListener(CART_UPDATED, this.onUpdated);
    document.addEventListener('click', this.onTrigger);

    this.addEventListener('click', (event) => {
      const step = event.target.closest?.('[data-delta]');
      if (step) {
        event.preventDefault();
        this.step(step);
      } else if (event.target.closest?.('[data-cart-close]') || this.onBackdrop(event)) {
        this.dialog.close();
      }
    });
    this.dialog?.addEventListener('close', () => this.returnFocus?.focus?.({ preventScroll: true }));
    if (this.dialog) this.dragToDismiss(this.dialog);
  }

  disconnectedCallback() {
    document.removeEventListener(CART_ADDED, this.onAdded);
    document.removeEventListener(CART_UPDATED, this.onUpdated);
    document.removeEventListener('click', this.onTrigger);
  }

  open(trigger = document.activeElement) {
    if (!this.dialog || this.dialog.open) return;
    this.returnFocus = trigger;
    this.dialog.showModal();
    this.querySelector('#CartDrawerTitle')?.focus();
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  /** A click on the dimmed page beside the drawer (the dialog's ::backdrop), not inside it. */
  onBackdrop(event) {
    if (event.target !== this.dialog) return false;
    const box = this.dialog.getBoundingClientRect();
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
  }

  async refresh() {
    const id = this.dataset.sectionId;
    // Ask in the current page's context: on /cart the drawer is the cart template's own section,
    // which Shopify only renders there. Anchored to the origin so a path starting with // stays on this host.
    const sections = await fetch(`${location.origin}${location.pathname}?sections=${encodeURIComponent(id)}`)
      .then((response) => response.json())
      .catch(() => null);
    const next = sections?.[id] && new DOMParser().parseFromString(sections[id], 'text/html').querySelector('[data-cart-body]');
    const body = next && this.querySelector('[data-cart-body]');
    if (!body) return;
    // Focus inside the old body would fall to the page; keep it in the drawer, on the title.
    const hadFocus = body.contains(document.activeElement);
    body.replaceWith(next);
    this.status = this.querySelector('[data-cart-status]');
    if (hadFocus) this.querySelector('#CartDrawerTitle')?.focus({ preventScroll: true });
  }

  /**
   * One tap on a stepper. The line shows the last quantity Shopify confirmed plus every step still
   * queued, at once; each queued tap then sends the confirmed quantity plus its own step, so a tap
   * behind a refused one is not built on the refused quantity. A refusal shows its message at once.
   * The bag is re-read only when the queue is empty, so the number never steps back mid-queue, and
   * a failed re-read (offline) is ignored: the queue always settles, so the next tap is sent.
   * A line on its way out (stepped to 0) takes no more taps.
   */
  step(link) {
    const line = link.closest('[data-cart-line]');
    if (line.hasAttribute('data-removing')) return this.queue;
    const quantity = line.querySelector('[data-qty]');
    line.dataset.confirmed ??= quantity.textContent;
    const delta = Number(link.dataset.delta);
    const show = () => {
      const shown = Math.max(0, Number(line.dataset.confirmed) + Number(line.dataset.pending ?? 0));
      quantity.textContent = String(shown);
      line.toggleAttribute('data-removing', shown === 0);
    };
    line.dataset.pending = String(Number(line.dataset.pending ?? 0) + delta);
    show();
    this.waiting = (this.waiting ?? 0) + 1;

    this.queue = this.queue.then(async () => {
      line.dataset.pending = String(Number(line.dataset.pending) - delta);
      const next = Math.max(0, Number(line.dataset.confirmed) + delta);
      try {
        await changeCartLine(link.dataset.key, next, { source: this });
        line.dataset.confirmed = String(next);
      } catch (error) {
        this.message = error.status === 422 ? error.message : (this.status?.dataset?.error ?? error.message);
        if (this.status) this.status.textContent = this.message;
      }
      show();
      if (--this.waiting) return;
      await this.refresh().catch(() => {});
      if (this.message && this.status) this.status.textContent = this.message;
      this.message = '';
    });
    return this.queue;
  }

  dragToDismiss(dialog) {
    let start = null;
    let history = [];
    dialog.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target.closest('a, button, input, [data-delta]')) return;
      start = { x: event.clientX, y: event.clientY, id: event.pointerId, dragging: false };
      history = [];
    });
    dialog.addEventListener('pointermove', (event) => {
      if (!start || event.pointerId !== start.id) return;
      const dx = event.clientX - start.x;
      if (!start.dragging) {
        if (Math.abs(dx) < 10 && Math.abs(event.clientY - start.y) < 10) return;
        if (Math.abs(dx) <= Math.abs(event.clientY - start.y)) return void (start = null);
        start.dragging = true;
        dialog.setPointerCapture(event.pointerId);
        dialog.style.transition = 'none';
      }
      // Right follows 1:1; left resists, since there is nowhere further to go.
      const x = dx > 0 ? dx : (dx * 0.55 * 120) / (120 + 0.55 * Math.abs(dx));
      dialog.style.transform = `translateX(${x}px)`;
      history = [...history.slice(-4), { x: dx, t: event.timeStamp }];
    });
    const release = (event) => {
      if (!start?.dragging || event.pointerId !== start.id) return void (start = null);
      const first = history[0];
      const last = history.at(-1);
      const velocity = first && last.t > first.t ? (last.x - first.x) / (last.t - first.t) : 0;
      dialog.style.transition = '';
      dialog.style.transform = '';
      if (last.x + project(velocity) > dialog.offsetWidth * 0.4) dialog.close();
      start = null;
    };
    dialog.addEventListener('pointerup', release);
    dialog.addEventListener('pointercancel', release);
  }
}

defineElement('cart-drawer', CartDrawer);
